import coverage from "@/constants/i18nCoverage.json";

const { routes } = coverage as { routes: Record<string, string[]> };

/**
 * Locales that have a real translation of this page. `i18n.js` falls back to
 * English for anything untranslated, so every other locale would serve a
 * byte-for-byte English duplicate — those must not be advertised as language
 * alternates or canonicalize to themselves.
 *
 * Routes absent from the map (auth, error pages, anything not listed in
 * `i18n.js`) have no translated variant, so only the default locale counts.
 */
export const getIndexableLocales = (
  path: string,
  defaultLocale: string,
): string[] => [defaultLocale, ...(routes[path] || [])];

export const isLocaleIndexable = (
  path: string,
  locale: string,
  defaultLocale: string,
): boolean => getIndexableLocales(path, defaultLocale).includes(locale);

export interface IUrlArgs {
  base: string;
  path: string;
  locale: string;
  defaultLocale: string;
  locales: string[];
}

export const getUrlWithLocale = ({
  base,
  path,
  locale,
  defaultLocale,
  locales,
}: IUrlArgs): string => {
  if (!base || !path || !locale || !defaultLocale) {
    throw new Error("Can't generate metadata");
  }

  let pageUrl = base;

  // add locale
  if (locale !== defaultLocale) {
    pageUrl += `/${locale}`;
  }

  // check if path already contains locale
  let pathToAppend = path;
  if (locales.includes(path.split("/")[1])) {
    pathToAppend = path.substring(3, path.length);
  }

  // remove trailing slash if any
  if (pathToAppend[pathToAppend.length - 1] === "/") {
    pathToAppend = pathToAppend.substring(0, pathToAppend.length - 1);
  }

  // append pathname
  pageUrl += pathToAppend;

  return pageUrl;
};
